import React, { useState } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import { Btn, TextInput } from "@app/shared/src/components_v2";
import { AuthFrame, AuthLabel, AuthLinks, AuthPanel } from "../layouts/AuthFrame";
import { useSession } from "../providers/SessionProvider";

/**
 * Director sign-in for Fleet Commander.
 *
 * Drivers never see this — the cab QR flow lives in the wzcab app and needs no login. That's
 * stated on the page because the most likely person to land here by accident is a driver who
 * typed the domain instead of scanning their cab card.
 *
 * The three links under the form are the whole account story for someone who is not yet signed
 * in: a reset when the password is lost, sign-up when there is no account, and the terms they are
 * agreeing to by using either.
 */
export const LoginPage: React.FC = () => {
    const { user, isChecking, login } = useSession();
    const location = useLocation() as { state?: { from?: string; email?: string; notice?: string } };
    const [email, setEmail] = useState(location.state?.email ?? "");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string>();

    if (!isChecking && user) {
        return <Navigate to={location.state?.from ?? "/overview"} replace />;
    }

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (busy) return;
        setBusy(true);
        setError(undefined);
        try {
            await login(email.trim(), password);
        } catch (err) {
            setError(err instanceof Error && err.message ? err.message : "Sign-in failed. Check your email and password.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <AuthFrame>
            {location.state?.notice && (
                <p className="mb-4 rounded-lg border border-[var(--line)] bg-[var(--surface)] px-4 py-3 text-[13px] leading-relaxed">
                    {location.state.notice}
                </p>
            )}

            <AuthPanel onSubmit={submit}>
                <AuthLabel>Email</AuthLabel>
                <TextInput
                    size="lg"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="mb-4"
                />
                <AuthLabel>Password</AuthLabel>
                <TextInput
                    size="lg"
                    type="password"
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="mb-4"
                />

                {error && <p className="mb-3 text-[12.5px] text-[var(--crit)]">{error}</p>}

                <Btn type="submit" size="lg" disabled={busy} className="w-full">
                    {busy ? "Signing in…" : "Sign in"}
                </Btn>
            </AuthPanel>

            <AuthLinks>
                <Link to="/forgot-password" state={{ email: email.trim() }} className="hover:text-[var(--text)]">
                    Forgot your password?
                </Link>
                <Link to="/create-account" className="hover:text-[var(--text)]">
                    Create a new account
                </Link>
                <Link to="/terms" className="hover:text-[var(--text)]">
                    Terms and Conditions
                </Link>
            </AuthLinks>

            <p className="mt-5 text-center text-[12px] leading-relaxed text-[var(--faint)]">
                Driving a truck? You don't need an account — scan the QR code on your cab card instead.
            </p>
        </AuthFrame>
    );
};
